// frontend/src/pages/admin/Products.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Save, X, Upload, ImageOff, RotateCcw, PackagePlus, PackageCheck, PackageMinus, Undo2 } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';
import { useCompany } from '../../utils/useCompany';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const EMPTY_FORM = {
  // name/description are the GREEK text and live on the product row; `translations`
  // carries the other languages (031). Kept as one form object so a half-saved
  // product — Greek in, English lost — is not a state the UI can produce.
  name: '', description: '', color_key: '', length_cm: '', weight_grams: '',
  texture_key: '', base_price_per_day: '', deposit_amount: '220', extra_day_price: '5',
  // Defaults to the daily rate, not the extension price: being late must cost
  // more than the shelf time it takes from us (032).
  late_fee_per_day: '', locker_id: '', locker_location: '', box_size: 'M', is_active: true,
  // GPSR Art. 19 (033). Blank rather than absent so the inputs stay controlled
  // from the first render, and because the server treats '' as "not supplied".
  // A product missing any of these can be SAVED — half-entered work is normal —
  // but it cannot be published; the server refuses is_active and says which
  // field is missing.
  // Ours by default — everything in this catalogue is sold under our own brand,
  // which under GPSR Art. 3(8) makes us the manufacturer whoever built it (038).
  own_brand: true,
  manufacturer_name: '', manufacturer_address: '', manufacturer_email: '',
  manufacturer_in_eu: false, model: '', batch_code: '',
  material_composition: '', country_of_origin: '', safety_warning: '',
  translations: { en: { name: '', description: '', safety_warning: '' } },
};

// The fields GPSR Art. 19 requires on an offer, in the order they are asked for
// in the form. Mirrors GPSR_REQUIRED in routes/products.js, which is the
// authority — this copy exists so the panel can grey out the "Active" switch and
// say why, instead of letting the operator find out from a 409.
const GPSR_FIELDS = ['manufacturer_name', 'manufacturer_address', 'manufacturer_email',
  'model', 'batch_code', 'material_composition', 'country_of_origin', 'safety_warning'];

// On an own-brand product these three are not typed here at all: the server
// answers them from the registered company identity, so counting them as
// "missing" would show a permanent three-field warning nobody can clear.
const TRADER_IDENTITY_FIELDS = ['manufacturer_name', 'manufacturer_address', 'manufacturer_email'];

const gpsrMissing = (form) => GPSR_FIELDS
  .filter((f) => !(form.own_brand && TRADER_IDENTITY_FIELDS.includes(f)))
  .filter((f) => !String(form[f] ?? '').trim());

// Mirrors utils/catalogVocabulary.js. The server validates against its own copy
// and answers 400 listing the allowed values, so this list is a convenience
// rather than the authority.
const COLOR_KEYS   = ['black', 'brown', 'brown_light', 'brown_dark', 'blonde_warm', 'blonde_ash'];
const TEXTURE_KEYS = ['straight', 'wavy', 'curly'];

// Pre-written choices for the two measurements, so a product is filled in by
// picking rather than typing (operator's request, 2026-09-16). The API takes
// any integer; these are the sizes the catalogue actually stocks.
const LENGTH_OPTIONS = [35, 40, 45, 50, 55, 60, 65, 70];
const WEIGHT_OPTIONS = [80, 100, 120, 140, 145, 160, 180, 200, 220, 250];

// box_size is not in the form any more. The cabinet still needs a size to pick
// a box, but every set the operator stocks takes the same one, so it rides on
// the 'M' default (BESTWOND_BOX_SIZES on the server is the authority).

// Where a product physically is (037). Derived from three columns rather than
// stored, so the row cannot claim two of these at once.
const stockState = (p) => {
  if (p.stocked_at) return 'in_box';
  if (p.stock_order_no) return 'pending';
  return 'none';
};

// Matches the server default (products.js /admin/all). The list previously showed
// only the first server page and printed its length as the total, so product
// #101+ could not be seen, priced, photographed or deactivated (audit H5/#43).
const PAGE_SIZE = 100;

const PRODUCT_STATUSES = ['available', 'rented', 'cleaning', 'maintenance'];
const PRODUCT_STATUS_KEYS = {
  available:   'statusAvailable',
  rented:      'statusRented',
  cleaning:    'statusCleaning',
  maintenance: 'statusMaintenance',
};
const productStatusLabel = (t, status) =>
  PRODUCT_STATUS_KEYS[status] ? t(`admin.products.${PRODUCT_STATUS_KEYS[status]}`) : status;

// The axios interceptor rejects with the response body FLATTENED — `err.response`
// is always undefined here, so the old `catch {}` blocks threw away the only
// explanation there was. When the API refuses to free a product it answers 409
// with error:'PRODUCT_HELD_BY_RENTAL' plus a `message` naming the rental that
// still holds it; `error` alone is a machine code, so prefer `message` when both
// are present. A raw network/timeout Error also carries `message` but no
// `error` — that one says "Network Error", so it stays behind the fallback.
const apiErrorText = (err, fallback) => {
  if (err && typeof err === 'object') {
    if (Array.isArray(err.errors) && err.errors[0]?.msg) return err.errors[0].msg;
    if (err.error) return err.message || err.error;
  }
  return fallback;
};

export default function AdminProducts() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // What the product is still missing before it can be offered. Recomputed on
  // every keystroke so the form can say it while the operator is typing rather
  // than after they press Save.
  const missingGpsr = gpsrMissing(form);
  // An own-brand product names US as its manufacturer, so it cannot be published
  // until the registered identity exists. The server enforces it; without this
  // the panel would count zero missing fields and the save would still come back
  // 409 — the surprise this whole block exists to prevent.
  const company = useCompany();
  const blockedOnCompany = form.own_brand && company.loaded && !company.configured;
  const [uploadingId, setUploadingId] = useState(null);
  const [deletingImage, setDeletingImage] = useState(null); // { productId, index }
  const [expandedImages, setExpandedImages] = useState(null); // productId whose images are shown
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  // The cabinets, for the machine picker in the form and for "load into" on
  // the row. One machine is the normal case and needs no choosing.
  const [machines, setMachines] = useState([]);
  // The save code for a loading that was just reserved — shown once, large,
  // with the steps. Closing it is fine: the code stays on the row.
  const [stockModal, setStockModal] = useState(null);
  const [stockBusy, setStockBusy] = useState(null);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const loadProducts = useCallback(() => {
    setLoading(true);
    // Endpoint is paginated and returns { products, count, limit, offset }.
    // The array fallback keeps this working against an older backend.
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
    api.get(`/products/admin/all?${params}`)
      .then(res => {
        const rows = Array.isArray(res) ? res : res.products || [];
        setProducts(rows);
        setCount(Number(res?.count) || rows.length);
      })
      .catch(() => toast.error(t('admin.loadError')))
      .finally(() => setLoading(false));
  }, [page, t]);

  // Loads on mount and whenever the page changes.
  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    api.get('/locker/admin/machines')
      .then((rows) => setMachines(Array.isArray(rows) ? rows : []))
      .catch(() => setMachines([]));
  }, []);
  const machineName = (p) => {
    const m = machines.find((x) => x.id === p.locker_id || x.unit_id === p.locker_id);
    return m ? m.name : (p.locker_id || '');
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const openAdd = () => { setEditProduct(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (p) => {
    setEditProduct(p);
    // box_size falls back so the <select> below never flips between controlled
    // and uncontrolled on a product saved before the field existed.
    setForm({
      ...p,
      length_cm: p.length_cm || '', weight_grams: p.weight_grams || '',
      box_size: p.box_size || 'M',
      locker_id: p.locker_id || '', locker_location: p.locker_location || '',
      late_fee_per_day: p.late_fee_per_day ?? '',
      // Same normalisation for the Art. 19 columns: a product saved before 033
      // has them NULL, and a null in a text input makes it uncontrolled.
      own_brand: p.own_brand !== false,
      manufacturer_name: p.manufacturer_name || '',
      manufacturer_address: p.manufacturer_address || '',
      manufacturer_email: p.manufacturer_email || '',
      manufacturer_in_eu: p.manufacturer_in_eu === true,
      model: p.model || '',
      batch_code: p.batch_code || '',
      material_composition: p.material_composition || '',
      country_of_origin: p.country_of_origin || '',
      safety_warning: p.safety_warning || '',
      // The API sends `translations` keyed by locale and omits a language that
      // has never been written. Normalised to a full shape so the inputs below
      // never flip between controlled and uncontrolled.
      translations: { en: {
        name: p.translations?.en?.name || '',
        description: p.translations?.en?.description || '',
        safety_warning: p.translations?.en?.safety_warning || '',
      } },
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditProduct(null); setForm(EMPTY_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct.id}`, form);
        toast.success(t('admin.products.updated'));
      } else {
        await api.post('/products', form);
        toast.success(t('admin.products.added'));
      }
      loadProducts();
      closeForm();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm(t('admin.products.deactivateConfirm'))) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success(t('admin.products.deactivated'));
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.failed')));
    }
  };

  // Withdrawal was a one-way door: the DELETE button only rendered for active
  // products, and the edit form re-sent whatever is_active it had read, so the
  // only way back into the catalogue was a manual UPDATE. products.js says as
  // much in its own comment — "the one product change an operator cannot undo
  // from the UI".
  //
  // Restoring deliberately forces 'maintenance' rather than putting the item
  // straight back on sale. A withdrawn set has been off the shelf: nobody knows
  // whether it is clean, or in its box, or in the building. POST /rentals
  // refuses anything that is not 'available', so this is the difference between
  // reappearing in the panel and becoming rentable — and the second one should
  // be a decision someone makes with the item in hand, via the status dropdown
  // that this restore is what re-enables.
  const handleRestore = async (id) => {
    if (!window.confirm(t('admin.products.restoreConfirm'))) return;
    try {
      await api.put(`/products/${id}`, { is_active: true, status: 'maintenance' });
      toast.success(t('admin.products.restored'));
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.failed')));
    }
  };

  const handleUpload = async (productId, file) => {
    if (!file) return;
    setUploadingId(productId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = localStorage.getItem('glamgo_token');
      const base = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${base}/products/${productId}/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        // Raw fetch, so the api interceptor never sees this — read the body here
        // or the reason (unsupported type, too large, product gone) is lost and
        // the admin just gets "Upload failed" for all of them.
        const body = await res.json().catch(() => null);
        const uploadErr = new Error(body?.message || body?.error || '');
        // Shaped like the api interceptor's rejections so apiErrorText treats it
        // the same way — and so a bodyless failure (no `error`) falls back to the
        // translated message instead of leaking an English Error string.
        uploadErr.error = body?.error;
        throw uploadErr;
      }
      toast.success(t('admin.products.photoUploaded'));
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.photoUploadFailed')));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteImage = async (productId, index) => {
    setDeletingImage({ productId, index });
    try {
      await api.delete(`/products/${productId}/image/${index}`);
      toast.success(t('admin.products.photoDeleted'));
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.photoDeleteFailed')));
    } finally {
      setDeletingImage(null);
    }
  };

  // ── The set's place in the cabinet (037) ──────────────────────────────────
  // Load: reserve a box; the machine answers with a save code the operator
  // types on the keypad. Confirm: the door closed on it (the machine's callback
  // does this by itself when it works; this is the hand-operated version).
  // Remove: it is out. Restore: a cancelled customer's box goes back on sale.
  const handleStockLoad = async (p) => {
    // Which machine: the product's own, else the only one there is.
    let lockerId = p.locker_id || (machines.length === 1 ? machines[0].id : null);
    if (!lockerId && machines.length > 1) {
      toast.error(t('admin.products.pickMachineFirst'));
      return;
    }
    setStockBusy(p.id);
    try {
      const res = await api.post(`/locker/admin/products/${p.id}/stock`, lockerId ? { locker_id: lockerId } : {});
      setStockModal({ product: p, ...res });
      loadProducts();
    } catch (err) {
      // LOADING_PENDING carries the code that is already out there — show it
      // rather than making the operator hunt for it in the row.
      if (err && err.error === 'LOADING_PENDING' && err.save_code) {
        setStockModal({ product: p, save_code: err.save_code, box_name: err.box_name, pending: true });
      } else {
        toast.error(apiErrorText(err, t('admin.products.failed')), { duration: 8000 });
      }
    } finally {
      setStockBusy(null);
    }
  };
  const handleStockConfirm = async (p) => {
    setStockBusy(p.id);
    try {
      await api.post(`/locker/admin/products/${p.id}/stock/confirm`);
      toast.success(t('admin.products.stockConfirmed'));
      setStockModal(null);
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.failed')), { duration: 8000 });
    } finally {
      setStockBusy(null);
    }
  };
  const handleStockRemove = async (p) => {
    if (!window.confirm(t('admin.products.stockRemoveConfirm'))) return;
    setStockBusy(p.id);
    try {
      await api.delete(`/locker/admin/products/${p.id}/stock`);
      toast.success(t('admin.products.stockRemoved'));
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.failed')), { duration: 8000 });
    } finally {
      setStockBusy(null);
    }
  };
  const handleStockRestore = async (p) => {
    if (!window.confirm(t('admin.products.stockRestoreConfirm'))) return;
    setStockBusy(p.id);
    try {
      const res = await api.post(`/locker/admin/products/${p.id}/stock/restore`);
      toast.success(t('admin.products.stockRestored', { box: res.box_name || '?' }));
      loadProducts();
    } catch (err) {
      toast.error(apiErrorText(err, t('admin.products.failed')), { duration: 8000 });
    } finally {
      setStockBusy(null);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/products/${id}`, { status });
      toast.success(t('admin.products.statusUpdated', { status: productStatusLabel(t, status) }));
      loadProducts();
    } catch (err) {
      // This is the 409 PRODUCT_HELD_BY_RENTAL case in practice: setting a
      // rented item back to 'available' would double-book it. The admin needs
      // to be told which rental is in the way, not just "Failed".
      toast.error(apiErrorText(err, t('admin.products.failed')), { duration: 8000 });
      // The <select> is uncontrolled with respect to the failure — reload so it
      // snaps back to the status the server actually still has.
      loadProducts();
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      {/* The upload spinner referenced `@keyframes spin`, which is defined
          nowhere in the project — the ring just sat there, frozen and looking
          like a rendering bug. */}
      <style>{`@keyframes gg-admin-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Sidebar */}
      <AdminSidebar />

      {/* Main */}
      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40 }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>{t('admin.products.title')}</h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>{t('admin.products.count', { count })}</p>
          </div>
          <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 8, background: theme.ink, color: theme.white, border: 'none', padding: '14px 24px', borderRadius: 2, cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em' }}>
            <Plus size={16} /> {t('admin.products.addProduct')}
          </button>
        </div>

        {/* Products table */}
        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: theme.cream }}>
                {['colProduct', 'colColorLength', 'colPriceDay', 'colDeposit', 'colLocker', 'colStatus', 'colActions'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 12, letterSpacing: '0.08em' }}>{t(`admin.products.${h}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: theme.gold }}>{t('admin.products.loading')}</td></tr>
              ) : products.map(p => (
                <React.Fragment key={p.id}>
                <tr style={{ borderBottom: expandedImages === p.id ? 'none' : `1px solid ${theme.creamDeep}`, opacity: p.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* Image strip — first 3 thumbs + upload */}
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {(p.image_urls?.length ? p.image_urls.slice(0, 3) : [null]).map((url, i) => (
                          <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                            {url ? (
                              <>
                                <img src={url} alt="" style={{ width: 44, height: 44, borderRadius: 4, objectFit: 'cover', display: 'block' }} />
                                <button
                                  onClick={() => handleDeleteImage(p.id, i)}
                                  disabled={!!(deletingImage?.productId === p.id && deletingImage?.index === i)}
                                  title={t('admin.products.deletePhoto')}
                                  style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: theme.red, border: `2px solid ${theme.white}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                                  <X size={10} color={theme.white} />
                                </button>
                              </>
                            ) : (
                              <div style={{ width: 44, height: 44, borderRadius: 4, background: theme.creamDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gold }}>
                                <ImageOff size={16} />
                              </div>
                            )}
                          </div>
                        ))}
                        {p.image_urls?.length > 3 && (
                          <button onClick={() => setExpandedImages(expandedImages === p.id ? null : p.id)}
                            style={{ width: 44, height: 44, borderRadius: 4, background: theme.creamDeep, border: `1px solid ${theme.sand}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: theme.grey400, flexShrink: 0 }}>
                            +{p.image_urls.length - 3}
                          </button>
                        )}
                        <label title={t('admin.products.uploadPhoto')} style={{ cursor: 'pointer', flexShrink: 0 }}>
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => handleUpload(p.id, e.target.files[0])}
                            onClick={e => e.target.value = ''} />
                          <div style={{ width: 44, height: 44, borderRadius: 4, background: theme.cream, border: `1px dashed ${theme.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gold }}>
                            {uploadingId === p.id
                              ? <div style={{ width: 14, height: 14, border: `2px solid ${theme.gold}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'gg-admin-spin 0.7s linear infinite' }} />
                              : <Upload size={14} />}
                          </div>
                        </label>
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, color: theme.ink }}>{p.name}</div>
                        <div style={{ color: theme.grey400, fontSize: 12 }}>{t(`catalog.texture.${p.texture_key}`, { defaultValue: p.texture_key })} · {t('admin.products.photosCount', { count: p.image_urls?.length || 0 })}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px', color: theme.grey600 }}>
                    {t(`catalog.color.${p.color_key}`, { defaultValue: p.color_key })}
                    {/* An untranslated product is invisible to an English
                        visitor as anything but Greek text, and the only place
                        that can be noticed is this list. */}
                    {!p.translations?.en?.name && (
                      <span title={t('admin.products.translationMissing')} style={{ marginLeft: 6, color: theme.amberDeep, fontSize: 11 }}>EN?</span>
                    )}
                    <br/><span style={{ fontSize: 12, color: theme.grey400 }}>{p.length_cm}cm · {p.weight_grams}g</span></td>
                  <td style={{ padding: '16px', fontFamily: theme.font.display, fontSize: 20 }}>€{parseFloat(p.base_price_per_day).toFixed(2)}</td>
                  <td style={{ padding: '16px', fontFamily: theme.font.display, fontSize: 18, color: theme.grey500 }}>€{parseFloat(p.deposit_amount).toFixed(0)}</td>
                  <td style={{ padding: '16px', color: theme.grey600, fontSize: 12 }}>
                    {/* Where the set is (037). 'available' only sells while it
                        is in a box, so this cell is the one that says whether
                        the row's status means anything to a customer. */}
                    {(() => {
                      const st = stockState(p);
                      const busy = stockBusy === p.id;
                      const booked = p.status === 'reserved' || p.status === 'rented';
                      const btn = (onClick, Icon, label, color) => (
                        <button onClick={onClick} disabled={busy} title={label}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '4px 8px', cursor: busy ? 'wait' : 'pointer', color, fontSize: 11, fontFamily: theme.font.body, marginRight: 6, marginTop: 6 }}>
                          <Icon size={12} /> {label}
                        </button>
                      );
                      if (st === 'in_box') return (
                        <>
                          <span style={{ display: 'inline-block', background: theme.greenWashSoft, border: `1px solid ${theme.green}`, borderRadius: 100, padding: '2px 10px', fontSize: 12, color: theme.green, fontWeight: 500 }}>
                            {t('admin.products.stockInBox', { box: p.stock_box_name || '?' })}
                          </span>
                          {machineName(p) && <div style={{ marginTop: 4, color: theme.grey400 }}>{machineName(p)}</div>}
                          {!booked && btn(() => handleStockRemove(p), PackageMinus, t('admin.products.stockRemove'), theme.grey600)}
                        </>
                      );
                      if (st === 'pending') return (
                        <>
                          <span style={{ display: 'inline-block', background: theme.creamDeep, border: `1px solid ${theme.amberDeep}`, borderRadius: 100, padding: '2px 10px', fontSize: 12, color: theme.amberDeep }}>
                            {t('admin.products.stockPending')}
                          </span>
                          <div style={{ marginTop: 4, fontFamily: theme.font.display, fontSize: 18, letterSpacing: '0.15em', color: theme.ink }}>{p.stock_save_code}</div>
                          <div style={{ color: theme.grey400 }}>{t('admin.products.stockInBox', { box: p.stock_box_name || '?' })}{machineName(p) ? ` · ${machineName(p)}` : ''}</div>
                          {btn(() => handleStockConfirm(p), PackageCheck, t('admin.products.stockConfirm'), theme.green)}
                          {btn(() => handleStockRemove(p), X, t('admin.products.stockCancel'), theme.grey400)}
                        </>
                      );
                      return (
                        <>
                          <span style={{ color: theme.grey400 }}>{t('admin.products.stockNone')}</span>
                          {p.status === 'available' && (
                            <div style={{ color: theme.amberDeep, fontSize: 11, marginTop: 2 }}>{t('admin.products.stockNotSellable')}</div>
                          )}
                          {p.is_active && !booked && btn(() => handleStockLoad(p), PackagePlus, t('admin.products.stockLoad'), theme.ink)}
                          {p.is_active && p.status === 'maintenance' && btn(() => handleStockRestore(p), Undo2, t('admin.products.stockRestore'), theme.grey600)}
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '16px' }}>
                    {/* A withdrawn product has no status worth setting: it is
                        out of the catalogue, and moving it to 'available' is what
                        used to tell its whole waitlist that something they can
                        never rent was back in stock. */}
                    {p.is_active ? (
                      <select value={p.status} onChange={e => handleStatusChange(p.id, e.target.value)} style={{ border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '6px 10px', fontSize: 12, background: theme.cream, cursor: 'pointer' }}>
                        {PRODUCT_STATUSES.map(s => <option key={s} value={s}>{productStatusLabel(t, s)}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize: 12, color: theme.grey400 }}>
                        {t('admin.products.withdrawn')}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEdit(p)} style={{ background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '6px 10px', cursor: 'pointer', color: theme.grey600 }}>
                        <Edit2 size={14} />
                      </button>
                      {p.is_active ? (
                        <button onClick={() => handleDeactivate(p.id)} title={t('admin.products.deactivateAction')} style={{ background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '6px 10px', cursor: 'pointer', color: theme.red }}>
                          <Trash2 size={14} />
                        </button>
                      ) : (
                        <button onClick={() => handleRestore(p.id)} title={t('admin.products.restoreAction')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '6px 10px', cursor: 'pointer', color: theme.green, fontSize: 12, fontFamily: theme.font.body }}>
                          <RotateCcw size={14} /> {t('admin.products.restoreAction')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedImages === p.id && p.image_urls?.length > 0 && (
                  <tr style={{ borderBottom: `1px solid ${theme.creamDeep}`, background: theme.cream }}>
                    <td colSpan={7} style={{ padding: '12px 16px 20px 72px' }}>
                      <div style={{ fontSize: 11, color: theme.grey400, marginBottom: 10, letterSpacing: '0.06em' }}>{t('admin.products.allPhotos', { count: p.image_urls.length })}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {p.image_urls.map((url, i) => (
                          <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                            <img src={url} alt="" style={{ width: 80, height: 80, borderRadius: 4, objectFit: 'cover', display: 'block' }} />
                            <button
                              onClick={() => handleDeleteImage(p.id, i)}
                              disabled={!!(deletingImage?.productId === p.id && deletingImage?.index === i)}
                              style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: theme.red, border: `2px solid ${theme.white}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                              <X size={11} color={theme.white} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && count > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.products.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, count),
                total: count,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.products.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= count}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= count ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= count ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.products.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading modal: the save code, once, large enough to read at the keypad. */}
      {stockModal && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.5), zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 460, padding: 36, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 24, margin: 0, textAlign: 'left' }}>{t('admin.products.stockModalTitle', { name: stockModal.product.name })}</h2>
              <button onClick={() => setStockModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400 }}><X size={20} /></button>
            </div>
            <div style={{ margin: '28px 0 8px', fontSize: 12, letterSpacing: '0.1em', color: theme.grey500 }}>{t('admin.products.stockModalCode')}</div>
            <div style={{ fontFamily: theme.font.display, fontSize: 56, letterSpacing: '0.25em', color: theme.ink, lineHeight: 1 }}>{stockModal.save_code}</div>
            {stockModal.box_name && (
              <div style={{ marginTop: 10, fontSize: 14, color: theme.grey600 }}>{t('admin.products.stockInBox', { box: stockModal.box_name })}{stockModal.locker_name ? ` · ${stockModal.locker_name}` : ''}</div>
            )}
            {stockModal.simulated && (
              <div style={{ marginTop: 8, fontSize: 11, color: theme.amberDeep }}>{t('admin.products.stockSimulated')}</div>
            )}
            <ol style={{ textAlign: 'left', margin: '24px 0 0', paddingLeft: 20, fontSize: 13, color: theme.grey600, lineHeight: 1.7 }}>
              <li>{t('admin.products.stockStep1')}</li>
              <li>{t('admin.products.stockStep2')}</li>
              <li>{t('admin.products.stockStep3')}</li>
            </ol>
            <button onClick={() => handleStockConfirm(stockModal.product)} disabled={stockBusy === stockModal.product.id}
              style={{ width: '100%', marginTop: 24, background: theme.ink, color: theme.white, border: 'none', padding: '14px', borderRadius: 2, cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
              {t('admin.products.stockConfirm')}
            </button>
            <p style={{ margin: '12px 0 0', fontSize: 11, color: theme.taupe }}>{t('admin.products.stockModalLater')}</p>
          </div>
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.5), zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', padding: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 28, margin: 0 }}>{editProduct ? t('admin.products.editTitle') : t('admin.products.addTitle')}</h2>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400 }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Greek is the site's default, so it lives on the product row
                    itself and is the fallback every other language falls back
                    to. It is required for that reason. */}
                <FormField label={t('admin.products.fieldNameEl')} value={form.name} onChange={v => setForm({...form, name: v})} required span={2} />
                <FormField
                  label={t('admin.products.fieldNameEn')}
                  value={form.translations.en.name}
                  onChange={v => setForm({ ...form, translations: { ...form.translations, en: { ...form.translations.en, name: v } } })}
                  span={2}
                  hint={form.translations.en.name.trim()
                    ? t('admin.products.translationHint')
                    : t('admin.products.translationMissing')}
                />
                <SelectField
                  label={t('admin.products.fieldColor')}
                  value={form.color_key}
                  onChange={v => setForm({...form, color_key: v})}
                  options={COLOR_KEYS}
                  labelFor={k => t(`catalog.color.${k}`, { defaultValue: k })}
                  placeholder={t('admin.products.choose')}
                  required
                />
                <SelectField
                  label={t('admin.products.fieldTexture')}
                  value={form.texture_key}
                  onChange={v => setForm({...form, texture_key: v})}
                  options={TEXTURE_KEYS}
                  labelFor={k => t(`catalog.texture.${k}`, { defaultValue: k })}
                  placeholder={t('admin.products.choose')}
                  required
                />
                <SelectField
                  label={t('admin.products.fieldLength')}
                  value={String(form.length_cm || '')}
                  onChange={v => setForm({...form, length_cm: v})}
                  options={LENGTH_OPTIONS.map(String)}
                  labelFor={v => `${v} cm`}
                  placeholder={t('admin.products.choose')}
                />
                <SelectField
                  label={t('admin.products.fieldWeight')}
                  value={String(form.weight_grams || '')}
                  onChange={v => setForm({...form, weight_grams: v})}
                  options={WEIGHT_OPTIONS.map(String)}
                  labelFor={v => `${v} g`}
                  placeholder={t('admin.products.choose')}
                />
                <FormField label={t('admin.products.fieldPricePerDay')} type="number" step="0.01" value={form.base_price_per_day} onChange={v => setForm({...form, base_price_per_day: v})} required />
                <FormField label={t('admin.products.fieldDeposit')} type="number" step="0.01" value={form.deposit_amount} onChange={v => setForm({...form, deposit_amount: v})} />
                <FormField label={t('admin.products.fieldExtraDay')} type="number" step="0.01" value={form.extra_day_price} onChange={v => setForm({...form, extra_day_price: v})} hint={t('admin.products.extraDayHint')} />
                <FormField label={t('admin.products.fieldLateFee')} type="number" step="0.01" value={form.late_fee_per_day}
                  onChange={v => setForm({...form, late_fee_per_day: v})}
                  placeholder={form.base_price_per_day || '15'}
                  hint={t('admin.products.lateFeeHint')} />
                {/* The machine the set lives in. The box NUMBER is not typed
                    here: the cabinet picks the box when the set is loaded and
                    tells us — see the locker column in the list. */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{t('admin.products.fieldMachine')}</label>
                  <select value={form.locker_id || ''}
                    onChange={e => {
                      const m = machines.find((x) => x.id === e.target.value);
                      setForm({
                        ...form, locker_id: e.target.value,
                        // The customer-facing place name follows the machine
                        // unless the operator has written their own.
                        locker_location: form.locker_location || (m ? [m.name, m.address].filter(Boolean).join(', ') : ''),
                      });
                    }}
                    style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white, cursor: 'pointer' }}>
                    <option value="">{machines.length ? t('admin.products.machineNone') : t('admin.products.machineNoneRegistered')}</option>
                    {machines.map(m => <option key={m.id} value={m.id}>{m.name}{m.is_active === false ? ' (off)' : ''}</option>)}
                  </select>
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.taupe }}>{t('admin.products.machineHint')}</p>
                </div>
                <FormField label={t('admin.products.fieldLockerLocation')} placeholder={t('admin.products.lockerLocationPlaceholder')} value={form.locker_location} onChange={v => setForm({...form, locker_location: v})} />
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{t('admin.products.descriptionLabelEl')}</label>
                  <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} style={{ width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{t('admin.products.descriptionLabelEn')}</label>
                  <textarea
                    value={form.translations.en.description}
                    onChange={e => setForm({ ...form, translations: { ...form.translations, en: { ...form.translations.en, description: e.target.value } } })}
                    rows={4}
                    style={{ width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
              </div>

              {/* ── EU product safety (GPSR Art. 19) ───────────────────────────
                  Its own block rather than eight more inputs in the grid above,
                  because it is one obligation and an operator needs to see it as
                  one thing: everything here has to be present before the product
                  can go live, and the panel says so up front instead of letting
                  the save come back 409. */}
              <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1px solid ${theme.sand}` }}>
                <h3 style={{ fontFamily: theme.font.display, fontSize: 18, fontWeight: 500, margin: '0 0 4px', color: theme.ink }}>
                  {t('admin.products.gpsrTitle')}
                </h3>
                <p style={{ margin: '0 0 6px', fontSize: 12, color: theme.grey500, lineHeight: 1.6 }}>{t('admin.products.gpsrIntro')}</p>
                {missingGpsr.length > 0 && (
                  <p style={{ margin: '0 0 18px', fontSize: 12, color: theme.redBrick, lineHeight: 1.6 }}>
                    {t('admin.products.gpsrMissing', { count: missingGpsr.length })}
                  </p>
                )}
                {blockedOnCompany && (
                  <p style={{ margin: '0 0 18px', fontSize: 12, color: theme.redBrick, lineHeight: 1.6 }}>
                    {t('admin.products.gpsrNeedsCompany')}
                  </p>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 20, marginTop: 12 }}>
                  {/* Who the manufacturer IS, before asking who they are. Sold
                      under our brand means we are it (Art. 3(8)), and then the
                      three fields below are our own registered details — served
                      from COMPANY_* so one change of address corrects every
                      product, instead of being retyped onto each one. */}
                  <label style={{ gridColumn: 'span 2', display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: theme.ink700, lineHeight: 1.6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.own_brand}
                      onChange={e => setForm({...form, own_brand: e.target.checked})}
                      style={{ marginTop: 3, flexShrink: 0 }} />
                    <span>{t('admin.products.ownBrand')}</span>
                  </label>

                  {form.own_brand ? (
                    <p style={{ gridColumn: 'span 2', margin: 0, fontSize: 12, color: theme.grey500, lineHeight: 1.6 }}>
                      {t('admin.products.ownBrandNote')}
                    </p>
                  ) : (<>
                    <FormField label={t('admin.products.fieldManufacturer')} value={form.manufacturer_name} onChange={v => setForm({...form, manufacturer_name: v})} />
                    <FormField label={t('admin.products.fieldManufacturerEmail')} type="email" value={form.manufacturer_email} onChange={v => setForm({...form, manufacturer_email: v})} />
                    <FormField label={t('admin.products.fieldManufacturerAddress')} value={form.manufacturer_address} onChange={v => setForm({...form, manufacturer_address: v})} span={2} />
                  </>)}
                  <FormField label={t('admin.products.fieldModel')} value={form.model} onChange={v => setForm({...form, model: v})} hint={t('admin.products.modelHint')} />
                  <FormField label={t('admin.products.fieldBatch')} value={form.batch_code} onChange={v => setForm({...form, batch_code: v})} hint={t('admin.products.batchHint')} />
                  <FormField label={t('admin.products.fieldMaterial')} value={form.material_composition} onChange={v => setForm({...form, material_composition: v})} hint={t('admin.products.materialHint')} />
                  <FormField label={t('admin.products.fieldOrigin')} value={form.country_of_origin} onChange={v => setForm({...form, country_of_origin: v})} />

                  {/* Decides whether the product page also has to name a
                      responsible person inside the Union (Art. 16) — which is
                      us, as importer. */}
                  {!form.own_brand && (
                    <label style={{ gridColumn: 'span 2', display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: theme.ink700, lineHeight: 1.6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.manufacturer_in_eu}
                        onChange={e => setForm({...form, manufacturer_in_eu: e.target.checked})}
                        style={{ marginTop: 3, flexShrink: 0 }} />
                      <span>{t('admin.products.manufacturerInEu')}</span>
                    </label>
                  )}

                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{t('admin.products.fieldWarningEl')}</label>
                    <textarea value={form.safety_warning} onChange={e => setForm({...form, safety_warning: e.target.value})} rows={4}
                      placeholder={t('admin.products.warningPlaceholder')}
                      style={{ width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }} />
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.taupe }}>{t('admin.products.warningHint')}</p>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{t('admin.products.fieldWarningEn')}</label>
                    <textarea
                      value={form.translations.en.safety_warning}
                      onChange={e => setForm({ ...form, translations: { ...form.translations, en: { ...form.translations.en, safety_warning: e.target.value } } })}
                      rows={4}
                      style={{ width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              <button type="submit" disabled={saving} style={{ width: '100%', marginTop: 24, padding: '16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Save size={16} /> {saving ? t('admin.products.saving') : t('admin.products.save')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, labelFor, placeholder, required, span }) {
  return (
    <div style={{ gridColumn: span === 2 ? 'span 2' : undefined }}>
      <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)} required={required}
        style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white, cursor: 'pointer' }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{labelFor(o)}</option>)}
      </select>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', required, span, step, placeholder, hint }) {
  return (
    <div style={{ gridColumn: span === 2 ? 'span 2' : undefined }}>
      <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{label}</label>
      <input type={type} step={step} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder}
        style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box' }} />
      {hint && <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.taupe }}>{hint}</p>}
    </div>
  );
}

